import React from 'react';
import { Chip, Link } from '@mui/material';
import {
  DirectionsRun,
  MenuBook,
  Place,
  ReportProblem,
  Restaurant,
  Storefront,
} from '@mui/icons-material';
import { buildGoogleMapsUrl } from '../../utils/externalLinks';
import {
  HouseContentSeverity,
  HouseContentStatus,
  HouseContentType,
  TranslateFunction,
} from './types';

export const getErrorMessage = (error: any, fallback: string): string => {
  const message = error?.response?.data?.message;

  if (Array.isArray(message)) {
    return message.join(', ');
  }

  if (typeof message === 'string') {
    return message;
  }

  return fallback;
};

export const renderLocationLink = (
  location?: string | null,
  latitude?: number | null,
  longitude?: number | null,
  stopPropagation = false,
  address?: string | null,
) => {
  const href = address?.trim()
    ? buildGoogleMapsUrl(null, null, address)
    : buildGoogleMapsUrl(latitude, longitude);

  const label = location || address?.trim() || (href ? `${latitude}, ${longitude}` : null);

  if (!label) {
    return null;
  }

  if (!href) {
    return label;
  }

  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      color="inherit"
      underline="hover"
      onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
    >
      {label}
    </Link>
  );
};

export const getStatusChip = (status: HouseContentStatus, t: TranslateFunction) => {
  switch (status) {
    case 'approved':
      return <Chip label={t('common.statuses.approved')} color="success" size="small" />;
    case 'pending':
      return <Chip label={t('common.statuses.pending')} color="warning" size="small" />;
    case 'rejected':
      return <Chip label={t('common.statuses.rejected')} color="error" size="small" />;
    case 'reported':
      return <Chip label={t('common.statuses.reported')} color="warning" size="small" />;
    case 'in_progress':
      return <Chip label={t('common.statuses.inProgress')} color="info" size="small" />;
    case 'resolved':
      return <Chip label={t('common.statuses.resolved')} color="success" size="small" />;
    default:
      return null;
  }
};

export const getSeverityChip = (
  severity: HouseContentSeverity | null | undefined,
  t: TranslateFunction,
) => {
  if (!severity) {
    return null;
  }

  if (severity === 'high') {
    return <Chip label={t('common.statuses.high')} color="error" size="small" />;
  }

  if (severity === 'medium') {
    return <Chip label={t('common.statuses.medium')} color="warning" size="small" />;
  }

  return <Chip label={t('common.statuses.low')} color="success" size="small" />;
};

export const getTypeIcon = (contentType: HouseContentType) => {
  if (contentType === 'guide') {
    return <MenuBook sx={{ fontSize: 56, color: 'primary.main' }} />;
  }

  if (contentType === 'shops') {
    return <Storefront sx={{ fontSize: 56, color: 'rgba(255,250,245,0.88)' }} />;
  }

  if (contentType === 'restaurants') {
    return <Restaurant sx={{ fontSize: 56, color: 'rgba(255,250,245,0.88)' }} />;
  }

  if (contentType === 'problem') {
    return <ReportProblem sx={{ fontSize: 56, color: 'primary.main' }} />;
  }

  return <DirectionsRun sx={{ fontSize: 58, color: 'rgba(255,250,245,0.88)' }} />;
};

export { Place };